import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUp, FiArrowUpRight, FiRefreshCw } from 'react-icons/fi';
import { useBot } from '../../context/BotContext';
import BotMascot from './BotMascot';

function MessageText({ text }) {
  // Render model output as text, including fenced code, without injecting HTML.
  return text.split(/(```[\s\S]*?```)/g).map((part, i) => part.startsWith('```')
    ? <pre key={i}><code>{part.replace(/^```[^\n]*\n?/, '').replace(/```$/, '')}</code></pre>
    : <span key={i}>{part}</span>);
}

export default function BotChat({ compact = false }) {
  const { messages, busy, mode, notice, page, send, clear } = useBot();
  const [draft, setDraft] = useState('');
  const scroll = useRef(null);
  const input = useRef(null);
  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [messages, busy]);
  const submit = (event) => {
    event.preventDefault();
    if (!draft.trim() || busy) return;
    send(draft);
    setDraft('');
    input.current?.focus();
  };
  const status = mode === 'ai' ? 'Live AI' : mode === 'checking' ? 'Connecting' : 'Guide mode';
  return <div className={`txbot-chat ${compact ? 'txbot-chat--compact' : ''}`}>
    <div className="txbot-chat-toolbar">
      <span className="txbot-status"><i aria-hidden="true" />{status}<span className="txbot-context"> / {page.name}</span></span>
      <button className="txbot-icon-button" type="button" onClick={clear} aria-label="Start a new conversation" title="New conversation"><FiRefreshCw /></button>
    </div>
    <div ref={scroll} className="txbot-messages" role="log" aria-label="Conversation with ThiranX Bot" aria-live="polite" aria-relevant="additions" tabIndex={0}>
      {messages.map(message => <div key={message.id} className={`txbot-message txbot-message--${message.role}`}>
        {message.role === 'assistant' && <BotMascot avatar />}
        <div className="txbot-message-content">
          <div className="txbot-message-label">{message.role === 'user' ? 'You' : 'ThiranX Bot'}{message.role === 'assistant' && message.id !== 'welcome' && <span>{message.mode === 'ai' ? 'AI' : 'Guide'}</span>}</div>
          <div className="txbot-message-text"><MessageText text={message.text} /></div>
          {message.links?.length > 0 && <div className="txbot-message-links">{message.links.filter(link => typeof link.to === 'string' && /^\/(?!\/)/.test(link.to)).map(link => <Link key={link.to} to={link.to}>{link.label}<FiArrowUpRight aria-hidden="true" /></Link>)}</div>}
        </div>
      </div>)}
      {busy && <div className="txbot-thinking" role="status"><BotMascot avatar /><span>Thinking with you<span className="txbot-dots" aria-hidden="true">•••</span></span></div>}
    </div>
    {notice && <p className="txbot-notice" role="status">{notice}</p>}
    <div className="txbot-suggestions" aria-label="Suggested questions">{page.prompts.slice(0, compact ? 2 : 3).map(prompt => <button key={prompt} type="button" disabled={busy} onClick={() => send(prompt)}>{prompt}<FiArrowUpRight aria-hidden="true" /></button>)}</div>
    <form className="txbot-composer" onSubmit={submit}>
      <label className="txbot-sr-only" htmlFor={compact ? 'bot-quick-message' : 'bot-studio-message'}>Ask ThiranX Bot</label>
      <textarea id={compact ? 'bot-quick-message' : 'bot-studio-message'} ref={input} rows={2} value={draft} maxLength={2000} placeholder="What’s on your learning mind?" onChange={event => setDraft(event.target.value)}
        onKeyDown={event => { if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) { event.preventDefault(); submit(event); } }} />
      <button type="submit" className="txbot-send" disabled={!draft.trim() || busy} aria-label="Send message"><FiArrowUp size={21} /></button>
    </form>
    <p className="txbot-chat-note">{mode === 'ai' ? 'AI can make mistakes. Confirm course and enrolment details on their pages.' : 'Built-in learning guidance. Live AI answers appear when connected.'} Conversation stays in this tab.</p>
  </div>;
}
